"use client";

import { DEFAULT_AVATAR } from "@constants/global";
import * as React from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

export function isRenderableImageSrc(src?: string | null): src is string {
  if (!src || src === DEFAULT_AVATAR) return false;

  return (
    src.startsWith("/") ||
    src.startsWith("http://") ||
    src.startsWith("https://") ||
    src.startsWith("data:")
  );
}

/** Never pass DEFAULT_AVATAR (sentinel) to <img>/background — it resolves as /app/... 404. */
export function resolveProfileImageSrc(
  src?: string | null,
  fallback?: string
): string | undefined {
  if (isRenderableImageSrc(src)) return src;
  return fallback;
}

const avatarSizes = {
  xs: { container: "h-5 w-5 min-w-5", text: "text-[8px]" },
  sm: { container: "h-7 w-7 min-w-7", text: "text-[10px]" },
  md: { container: "h-10 w-10 min-w-10", text: "text-sm" },
  lg: { container: "h-14 w-14 min-w-14", text: "text-lg" },
  xl: { container: "h-20 w-20 min-w-20", text: "text-2xl" },
};

export interface AvatarProps {
  src?: string | null;
  name: string;
  size?: keyof typeof avatarSizes;
  title?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  className?: never;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export function Avatar({
  src,
  name,
  size = "md",
  title,
  subtitle,
  className,
}: AvatarProps) {
  const [imgError, setImgError] = React.useState(false);
  const sizeConfig = avatarSizes[size];

  React.useEffect(() => {
    setImgError(false);
  }, [src]);

  const avatarElement = (
    <div
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        "bg-gradient-to-br from-indigo-100 to-violet-100 dark:from-indigo-900/40 dark:to-violet-900/40",
        sizeConfig.container,
        className
      )}
    >
      {isRenderableImageSrc(src) && !imgError ? (
        <Image
          src={src}
          alt={name}
          fill
          className="object-cover"
          onError={() => setImgError(true)}
          sizes="80px"
        />
      ) : (
        <span
          className={cn(
            "font-semibold text-indigo-700 dark:text-indigo-300",
            sizeConfig.text
          )}
        >
          {getInitials(name)}
        </span>
      )}
    </div>
  );

  if (!title) return avatarElement;

  return (
    <div className="flex items-center gap-2.5">
      {avatarElement}
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-slate-900 dark:text-white">
          {title}
        </div>
        {subtitle && (
          <div className="truncate text-xs text-slate-500 dark:text-zinc-400">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
}
